import { PageHeader } from '@/components/layout/PageHeader';
import { PageShell } from '@/components/layout/PageShell';
import { Button } from '@/components/ui/button';
import { CopyButton } from '@/components/ui/copy-button';
import { ApiError, api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { type DataSource, type DataSourceKind, isDataSourceKindSupported } from '@innermate/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Database, FileSpreadsheet, Plus, RefreshCw, Trash2 } from 'lucide-react';

const KIND_LABELS: Record<DataSourceKind, string> = {
  postgres: 'PostgreSQL',
  mysql: 'MySQL',
  mariadb: 'MariaDB',
  mssql: 'MSSQL',
  oracle: 'Oracle',
  bigquery: 'BigQuery',
  excel: 'Excel·CSV',
  tableau: 'Tableau',
  duckdb: 'DuckDB',
};

export function AdminDataSourcesList() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'data-sources'],
    queryFn: () => api.dataSourceList(),
    refetchInterval: 5000,
  });

  const syncMutation = useMutation({
    mutationFn: (id: string) => api.dataSourceSync(id, { scope: 'all' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'data-sources'] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.dataSourceDelete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'data-sources'] }),
  });

  const items = data?.dataSources ?? [];

  return (
    <PageShell
      header={
        <PageHeader
          title="데이터 소스"
          count={items.length}
          description="사용자 챗에서 사용할 외부 데이터베이스를 등록하고 테이블/컬럼 설명을 관리합니다."
          actions={
            <div className="inline-flex items-center gap-2">
              <Button
                variant="secondary"
                onClick={() => navigate({ to: '/admin/data-sources/new-excel' })}
              >
                <FileSpreadsheet className="size-4" />
                Excel·CSV 업로드
              </Button>
              <Button
                variant="secondary"
                onClick={() => navigate({ to: '/admin/data-sources/new-tableau' })}
              >
                <Database className="size-4" />
                Tableau 추가
              </Button>
              <Button
                variant="secondary"
                onClick={() => navigate({ to: '/admin/data-sources/new-bigquery' })}
              >
                <Database className="size-4" />
                BigQuery 추가
              </Button>
              <Button onClick={() => navigate({ to: '/admin/data-sources/new' })}>
                <Plus className="size-4" />
                RDBMS 추가
              </Button>
            </div>
          }
        />
      }
    >
      {isLoading ? (
        <p className="text-sm text-foreground-muted">불러오는 중…</p>
      ) : error ? (
        <p className="text-sm text-destructive">
          {error instanceof ApiError ? error.message : '데이터소스 목록을 불러오지 못했습니다.'}
        </p>
      ) : items.length === 0 ? (
        <EmptyState onCreate={() => navigate({ to: '/admin/data-sources/new' })} />
      ) : (
        <div className="rounded-2xl border border-border bg-surface overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-surface-muted/40 text-[11px] uppercase tracking-wider text-foreground-muted">
              <tr>
                <Th>이름</Th>
                <Th>종류</Th>
                <Th>호스트</Th>
                <Th align="right">테이블</Th>
                <Th align="right">컬럼</Th>
                <Th>마지막 구조 sync</Th>
                <Th>상태</Th>
                <Th align="right">액션</Th>
              </tr>
            </thead>
            <tbody>
              {items.map((ds) => (
                <Row
                  key={ds.id}
                  ds={ds}
                  onOpen={() =>
                    navigate({
                      to: '/admin/data-sources/$id',
                      params: { id: ds.id },
                    })
                  }
                  onSync={(e) => {
                    e.stopPropagation();
                    syncMutation.mutate(ds.id);
                  }}
                  onDelete={(e) => {
                    e.stopPropagation();
                    if (
                      window.confirm(
                        `"${ds.name}" 를 삭제하시겠습니까? 저장된 메타·설명이 모두 사라집니다.`,
                      )
                    ) {
                      deleteMutation.mutate(ds.id);
                    }
                  }}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </PageShell>
  );
}

function Th({ children, align = 'left' }: { children: React.ReactNode; align?: 'left' | 'right' }) {
  return (
    <th
      scope="col"
      className={cn('px-4 py-2.5 font-medium', align === 'right' ? 'text-right' : 'text-left')}
    >
      {children}
    </th>
  );
}

function EmptyState({ onCreate }: { onCreate: () => void }) {
  return (
    <div className="rounded-2xl border border-dashed border-border bg-surface/50 px-8 py-16 text-center">
      <Database className="size-8 mx-auto text-foreground-muted" />
      <h3 className="mt-3 text-base font-medium">등록된 데이터소스가 없습니다.</h3>
      <p className="mt-1 text-sm text-foreground-muted">
        첫 데이터소스를 등록하면 구조 sync 가 자동으로 실행됩니다.
      </p>
      <Button onClick={onCreate} className="mt-4">
        <Plus className="size-4" />
        데이터소스 추가
      </Button>
    </div>
  );
}

function Row({
  ds,
  onOpen,
  onSync,
  onDelete,
}: {
  ds: DataSource;
  onOpen: () => void;
  onSync: (e: React.MouseEvent) => void;
  onDelete: (e: React.MouseEvent) => void;
}) {
  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: 행 전체 클릭으로 상세 — 키보드 사용자는 행 안의 액션 버튼으로 접근
    <tr
      className="border-t border-border hover:bg-surface-muted/30 cursor-pointer"
      onClick={onOpen}
    >
      <td className="px-4 py-3">
        <div className="group inline-flex items-center gap-1">
          <span className="font-medium">{ds.name}</span>
          <CopyButton
            text={ds.name}
            label={`${ds.name} 복사`}
            className="opacity-0 group-hover:opacity-100 group-focus-within:opacity-100"
          />
        </div>
        {ds.description ? (
          <div className="mt-0.5 text-xs text-foreground-muted truncate max-w-[320px]">
            {ds.description}
          </div>
        ) : null}
      </td>
      <td className="px-4 py-3">
        <KindBadge kind={ds.kind} />
      </td>
      <td className="px-4 py-3 font-mono text-xs text-foreground-muted">
        {ds.kind === 'excel' ? (
          <span className="text-foreground-muted">(엑셀 업로드)</span>
        ) : ds.kind === 'duckdb' ? (
          <span className="text-foreground-muted">(태블로 통합 산출물)</span>
        ) : ds.kind === 'tableau' ? (
          <>
            {ds.host}
            <span className="ml-1 text-foreground-muted">
              {ds.database === '_default' ? '· 기본 사이트' : `· site=${ds.database}`}
            </span>
          </>
        ) : (
          <>
            {ds.host}:{ds.port}/{ds.database}
          </>
        )}
      </td>
      <td className="px-4 py-3 text-right tabular-nums">{ds.tableCount}</td>
      <td className="px-4 py-3 text-right tabular-nums">{ds.columnCount}</td>
      <td className="px-4 py-3 text-xs text-foreground-muted">
        {ds.structureSyncedAt ? <RelativeTime iso={ds.structureSyncedAt} /> : '미실행'}
      </td>
      <td className="px-4 py-3">
        {ds.lastSyncError ? (
          <span className="text-xs text-destructive" title={ds.lastSyncError}>
            오류
          </span>
        ) : ds.structureSyncedAt ? (
          <span className="text-xs text-[hsl(150_60%_45%)]">정상</span>
        ) : (
          <span className="text-xs text-foreground-muted">대기</span>
        )}
      </td>
      <td className="px-4 py-3 text-right">
        <div className="inline-flex items-center gap-1">
          <button
            type="button"
            onClick={onSync}
            aria-label="다시 sync"
            className="size-8 inline-grid place-items-center rounded-md text-foreground-muted hover:bg-surface-muted hover:text-foreground"
          >
            <RefreshCw className="size-4" />
          </button>
          <button
            type="button"
            onClick={onDelete}
            aria-label="삭제"
            className="size-8 inline-grid place-items-center rounded-md text-foreground-muted hover:bg-surface-muted hover:text-destructive"
          >
            <Trash2 className="size-4" />
          </button>
        </div>
      </td>
    </tr>
  );
}

function KindBadge({ kind }: { kind: DataSourceKind }) {
  const supported = isDataSourceKindSupported(kind);
  return (
    <span
      className={cn(
        'inline-flex items-center px-2 h-5 rounded-sm text-[10px] uppercase tracking-wider',
        supported ? 'bg-accent/15 text-accent' : 'bg-surface-muted text-foreground-muted',
      )}
      title={supported ? undefined : '준비 중'}
    >
      {KIND_LABELS[kind]}
    </span>
  );
}

function RelativeTime({ iso }: { iso: string }) {
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000) return <>{Math.max(1, Math.floor(diff / 1000))}초 전</>;
  if (diff < 3_600_000) return <>{Math.floor(diff / 60_000)}분 전</>;
  if (diff < 86_400_000) return <>{Math.floor(diff / 3_600_000)}시간 전</>;
  return <>{Math.floor(diff / 86_400_000)}일 전</>;
}
